import { toast } from '@design-system/index'
import { makeAutoObservable } from 'mobx'
import React from 'react'
import { v4 as uuidv4 } from 'uuid'

import { TOAST_STR } from '@/constant'
import ConfirmModal from '@/contentComponents/modal/ConfirmModal'
import type { ConfirmModalProps } from '@/contentComponents/modal/ConfirmModal/ConfirmModal'
import {
  ErrorReportArithmetic,
  ErrorReportConcept,
  ErrorReportProblem,
} from '@/contentComponents/modal/ErrorReport'
import LoadingModal from '@/contentComponents/modal/LoadingModal'
import type { LoadingModalProps } from '@/contentComponents/modal/LoadingModal/LoadingModal'
import FirstLoginWelcomeModal from '@/contentComponents/modal/FirstLoginWelcomeModal'
import WarningModal from '@/contentComponents/modal/WarningModal'
import 학습지교재생성제한모달 from '@/contentComponents/modal/학습지교재생성제한모달'
import { 담당권한불가 } from '@/contentComponents/permission/담당권한불가'
import useRepository from '@/hooks/useRepository'
import commonRepo from '@/repository/common.repo'
import navigationUtils from '@/utils/navigationUtils'

import ErrorReportService from './errorReport.service'

export type ModalOption = {
  hasCloseButton?: boolean
  closeButtonPlacement?: 'left' | 'right'
  hasBottomCloseButton?: boolean
  isCloseable?: boolean
  modalStyle?: {
    overlay?: React.CSSProperties
    content?: React.CSSProperties
  }
  modalStyleTheme?: 'common' | 'content' | 'video'
  onClose?: (...args: any[]) => boolean
  onCloseButtonClick?: () => void
  useCloseButtonOutsideStyle?: boolean
  closeButtonCustomStyle?: React.CSSProperties
  modalTitle?: string
  doNotShowAgainOption?: boolean
  onDoNotShowAgainOptionChange?: (checked: boolean) => void
  modalContentRadius?: string
}

type WarningModalContents = {
  content: React.ReactNode
  actionButtonText?: string
  cancelButtonText?: string
  onClick: React.MouseEventHandler<HTMLButtonElement>
  onCancelClick?: React.MouseEventHandler<HTMLButtonElement>
}

class ModalState {
  modalContent: React.ReactNode
  modalTitle: string
  opt: ModalOption = {
    hasCloseButton: true,
    hasBottomCloseButton: false,
    isCloseable: true,
    modalStyleTheme: 'common',
    useCloseButtonOutsideStyle: false,
    doNotShowAgainOption: false,
    closeButtonPlacement: 'left',
  }

  constructor(modalContent: React.ReactNode, opt?: ModalOption) {
    this.modalContent = modalContent
    this.opt = { ...this.opt, ...opt }
    this.modalTitle =
      opt?.modalTitle ??
      modalContent?.valueOf()?.['type']?.['displayName'] ??
      modalContent?.valueOf()?.['type']?.['type']?.['displayName'] ??
      uuidv4()
  }
}

class ModalService {
  modalList: ModalState[] = []
  modalOffsetY: number | null = null

  constructor() {
    makeAutoObservable(this)
  }

  get getModalListTitles() {
    return this.modalList.map(({ modalTitle }) => modalTitle).toString()
  }

  setModalOffsetY = (offset: number) => {
    this.modalOffsetY = offset
  }

  resetModalOffsetY = () => {
    this.modalOffsetY = null
  }

  openModal = (ModalContent: React.ReactNode, opt?: ModalOption) => {
    const modal = new ModalState(ModalContent, opt)
    this.modalList.push(modal)
  }

  closeModal = (...args: any[]) => {
    const modal = this.modalList.pop()

    if (modal?.opt.onClose) {
      modal?.opt.onClose(...args)
    }

    this.resetModalOffsetY()
  }

  closeAllModal = (opt?: { invokeOnClose?: boolean }) => {
    if (opt?.invokeOnClose) {
      this.modalList.forEach(() => {
        this.closeModal()
      })
    }
    this.modalList = []
    this.resetModalOffsetY()
  }

  openLoadingModal = (loadingModalContents: LoadingModalProps, opt?: ModalOption) => {
    this.modalList.push(
      new ModalState(<LoadingModal {...loadingModalContents} />, {
        hasCloseButton: false,
        ...opt,
      }),
    )
  }

  openConfirmModal = (confirmModalContents: ConfirmModalProps, opt?: ModalOption) => {
    this.modalList.push(
      new ModalState(
        <ConfirmModal {...confirmModalContents} />,
        Object.assign(
          {
            hasCloseButton: true,
          },
          opt,
        ),
      ),
    )
  }

  openWarningModal = (warningModalContents: WarningModalContents, opt?: ModalOption) => {
    this.modalList.push(
      new ModalState(<WarningModal {...warningModalContents} />, {
        hasCloseButton: false,
        ...opt,
      }),
    )
  }

  openVideoModal = (videoContent: React.ReactNode, opt?: ModalOption) => {
    this.modalList.push(
      new ModalState(videoContent, {
        hasCloseButton: true,
        modalStyleTheme: 'video',
        closeButtonCustomStyle: {
          position: 'absolute',
          top: -32,
          right: 0,
          color: 'var(--White)',
        },
        ...opt,
      }),
    )
  }

  //예외: 자이스토리의 경우 시중교재지만, 해설이 있음. 자이스토리의 경우 시중교재이지만 자체교재 신고 로직과 같음.(자이스토리 : PROBLEM_CUSTOM)
  openErrorReportModal = <
    T extends Exclude<ErrorReportService['type'], undefined> = 'PROBLEM_PUBLIC',
  >(
    categoryId: number,
    type: T,
    ...args: T extends 'PROBLEM_PUBLIC' | 'PROBLEM_PUBLIC_ANSWER_NOT_CONFIRMED' | 'PROBLEM_CUSTOM'
      ? [additionalInfo: req.BugReportData['additionalInfo']]
      : [additionalInfo?: undefined]
  ) => {
    const service = useRepository(ErrorReportService, type, categoryId, ...args)
    switch (type) {
      case 'ARITHMETIC': // T9-3 테마별 추천학습지-연산-문제오류신고
        this.modalList.push(
          new ModalState(<ErrorReportArithmetic categoryId={categoryId} />, {
            onClose: () => {
              service.clearErrorReport()
              return true
            },
          }),
        )
        break
      // T5-2 수업준비-학습지 만들기 2단계-문제 종류가 '개념', '소단원별' 일 때-개념오류신고
      case 'LITTLE_CHAPTER':
        this.modalList.push(
          new ModalState(<ErrorReportConcept categoryId={categoryId} type="LITTLE_CHAPTER" />, {
            onClose: () => {
              service.clearErrorReport()
              return true
            },
          }),
        )
        break
      case 'CONCEPT':
        this.modalList.push(
          new ModalState(<ErrorReportConcept categoryId={categoryId} type="CONCEPT" />, {
            onClose: () => {
              service.clearErrorReport()
              return true
            },
          }),
        )
        break
      case 'PROBLEM_PUBLIC': // T12 수업-교재-시중교재선택 후-정답영역클릭 시-문제오류신고됨
      case 'PROBLEM_PUBLIC_ANSWER_NOT_CONFIRMED': // PROBLEM_PUBLIC 와 같지만 정답 확인 안된 문제 신고 케이스
      case 'PROBLEM_CUSTOM': // T5-2 수업준비-학습지 만들기 2단계-문제 종류가 '문제'일 때-문제오류신고 / T13 수업-학습지-문제오류신고 / T12 수업-교재-자체교재선택 후-정답영역 클릭시 열리는 해설영역의 신고버튼 클릭 시- 문제오류신고됨
        this.modalList.push(
          new ModalState(
            <ErrorReportProblem categoryId={categoryId} type={type} additionalInfo={args[0]} />,
            {
              onClose: () => {
                service.clearErrorReport()
                return true
              },
              modalStyle: {
                content: {
                  overflow: 'auto',
                },
              },
            },
          ),
        )
        break
      default:
        return
    }
  }

  // 미지원 기능 안내 모달 - 임시
  openNotSupportUtil = () => {
    this.openConfirmModal({
      content: (
        <>
          <p>준비 중인 기능입니다.</p>
          <p>곧 오픈 예정이니 잠시만 기다려주세요!</p>
        </>
      ),
      actionButtonText: '확인',
      onClick: this.closeModal,
      hasCancelButton: false,
    })
  }

  openNotAccessPermissionModal = () => {
    this.openConfirmModal(
      {
        content: <담당권한불가 />,
        onClick: modalService.closeModal,
        hasCancelButton: false,
      },
      { hasCloseButton: false },
    )
  }

  // 이미 삭제된 교제, 학습지를 출제, 숙제, 등등 작업할 때 경고
  openDeletedBookModal = () => {
    this.openConfirmModal(
      {
        content: <p>이미 출제 취소 또는 삭제된 학습지, 교재 입니다.</p>,
        onClick: () => {
          navigationUtils.reload()
        },
        actionButtonText: '확인',
        hasCancelButton: false,
      },
      { hasCloseButton: false },
    )
  }

  openFirstLoginWelcomeModal = () => {
    this.openConfirmModal(
      {
        content: <FirstLoginWelcomeModal />,
        onClick: this.closeModal,
        hasCancelButton: false,
        actionButtonText: '확인',
      },
      { hasCloseButton: false },
    )
  }

  createLimitModal(type: '학습지' | '교재' = '학습지') {
    this.openConfirmModal(
      {
        title: `고객님, ${commonRepo.appKorName}에 푹 빠지셨군요!`,
        content: <학습지교재생성제한모달 />,
        onClick: () => {
          toast.failure(
            type === '학습지' ? TOAST_STR.createWorksheetError : TOAST_STR.createWorkbookError,
          )
          modalService.closeModal()
        },
        hasCancelButton: false,
        actionButtonText: '확인했어요',
      },
      { hasCloseButton: false },
    )
  }
}

const modalService = new ModalService()
export default modalService
